export type EmbedProvider = "youtube" | "vimeo";

export interface ParsedEmbed {
  provider: EmbedProvider;
  videoId: string;
  url: string;
}

export interface PlayerSettings {
  playerAutoplay: boolean;
  playerControls: boolean;
  playerMuted: boolean;
  playerLoop: boolean;
}

export const DEFAULT_PLAYER_SETTINGS: PlayerSettings = {
  playerAutoplay: false,
  playerControls: true,
  playerMuted: false,
  playerLoop: false,
};

/** Parses a YouTube or Vimeo URL into provider + video id. */
export function parseEmbedUrl(raw: string): ParsedEmbed | null {
  if (!raw || typeof raw !== "string") return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, "").toLowerCase();

  if (host === "youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0];
    return id ? { provider: "youtube", videoId: id, url: raw.trim() } : null;
  }
  if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "youtube-nocookie.com"
  ) {
    const v = url.searchParams.get("v");
    if (v) return { provider: "youtube", videoId: v, url: raw.trim() };
    const parts = url.pathname.split("/").filter(Boolean);
    if (["embed", "shorts", "v"].includes(parts[0]) && parts[1]) {
      return { provider: "youtube", videoId: parts[1], url: raw.trim() };
    }
    return null;
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const parts = url.pathname.split("/").filter(Boolean);
    const id = [...parts].reverse().find((p) => /^\d+$/.test(p));
    return id ? { provider: "vimeo", videoId: id, url: raw.trim() } : null;
  }

  return null;
}

/** CSS aspect-ratio for embed wrappers (YouTube Shorts → portrait). */
export function resolveEmbedAspectRatio(embedUrl?: string): string {
  if (!embedUrl) return "16 / 9";

  try {
    const url = new URL(embedUrl.trim());
    const parts = url.pathname.split("/").filter(Boolean);
    if (parts[0] === "shorts") return "9 / 16";
  } catch {
    // default landscape
  }

  return "16 / 9";
}

/**
 * Static preview thumbnail for an embed (no iframe, no autoplay).
 * YouTube exposes a stable image endpoint; Vimeo has no public one without an
 * API call, so it falls back to null (caller shows a generic placeholder).
 */
export function resolveEmbedThumbnailUrl(
  provider?: string,
  videoId?: string,
  embedUrl?: string,
): string | null {
  const resolved = resolveEmbedIds(provider, videoId, embedUrl);
  if (!resolved.videoId) return null;
  if (resolved.provider === "youtube") {
    return `https://i.ytimg.com/vi/${resolved.videoId}/hqdefault.jpg`;
  }
  return null;
}

export function resolveEmbedIds(
  provider?: string,
  videoId?: string,
  embedUrl?: string,
): { provider?: EmbedProvider; videoId?: string } {
  if (provider && videoId) {
    return { provider: provider as EmbedProvider, videoId };
  }
  if (embedUrl) {
    const parsed = parseEmbedUrl(embedUrl);
    if (parsed) return { provider: parsed.provider, videoId: parsed.videoId };
  }
  return {};
}

/** Builds the provider embed URL with query params from CMS player settings. */
export function buildEmbedSrc(
  provider: string | undefined,
  videoId: string | undefined,
  settings: PlayerSettings,
  origin?: string,
): string | null {
  if (!videoId) return null;

  if (provider === "youtube") {
    const params = new URLSearchParams();
    if (settings.playerAutoplay) {
      params.set("autoplay", "1");
      params.set("mute", "1");
    } else if (settings.playerMuted) {
      params.set("mute", "1");
    }
    params.set("controls", settings.playerControls ? "1" : "0");
    if (settings.playerLoop) {
      params.set("loop", "1");
      params.set("playlist", videoId);
    }
    // Ne pas passer `origin=http://localhost…` : YouTube le refuse parfois en
    // Error 153. Uniquement les origines https (prod / preprod).
    if (origin && origin.startsWith("https://")) {
      params.set("origin", origin);
    }
    // Domaine officiel (pas nocookie) : moins de rejets « Error 153 » côté player.
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  }

  if (provider === "vimeo") {
    const params = new URLSearchParams();
    if (settings.playerAutoplay) {
      params.set("autoplay", "1");
      params.set("muted", "1");
    } else if (settings.playerMuted) {
      params.set("muted", "1");
    }
    params.set("controls", settings.playerControls ? "1" : "0");
    if (settings.playerLoop) params.set("loop", "1");
    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
  }

  return null;
}
